export const PEOPLE_WORKSPACE_VIEWS = [
  {
    id: 'applicants',
    label: 'Applicants',
    href: '/portal/recruitment',
    title: 'Applicants',
    description: 'Review applicant records, recruitment stages, interviews, and pre-onboarding checks.',
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    href: '/portal/employees?stage_preset=ONBOARDING',
    title: 'Onboarding',
    description: 'Track approved people who are completing onboarding before activation.',
  },
  {
    id: 'employees',
    label: 'Employees',
    href: '/portal/employees',
    title: 'Employees',
    description: 'Manage active staff records, compliance progress, documents, and lifecycle actions.',
  },
  {
    id: 'staff_profiles',
    label: 'Staff profiles',
    href: '/portal/staff-introductions',
    title: 'Staff profiles',
    description: 'Secure, client-specific one-page staff introductions.',
    managerOnly: true,
  },
  {
    id: 'communications',
    label: 'Staff communications',
    href: '/portal/staff-communications',
    title: 'Staff communications',
    description: 'Manage employee-domain email conversations without mixing them into Recruitment history.',
  },
  {
    id: 'inactive',
    label: 'Inactive & Archived',
    href: '/portal/employees?stage_preset=INACTIVE&archived=true',
    title: 'Inactive & Archived',
    description: 'Review inactive, archived, withdrawn, and terminal people records.',
  },
];

const EMPLOYEE_ROUTE = '/portal/employees';
const RECRUITMENT_ROUTE = '/portal/recruitment';
const STAFF_COMMUNICATIONS_ROUTE = '/portal/staff-communications';
const STAFF_INTRODUCTIONS_ROUTE = '/portal/staff-introductions';

const normalizeSearch = (search) => {
  if (!search) return '';
  return search.startsWith('?') ? search.slice(1) : search;
};

const matchesValue = (value, expected) => String(value || '').toLowerCase() === expected;

export const deriveStagePresetFilterState = (preset, options = {}) => {
  const normalizedPreset = String(preset || 'ALL').toUpperCase();
  const archived = Boolean(options.archived);

  if (normalizedPreset === 'ONBOARDING') {
    return {
      stagePreset: 'ONBOARDING',
      statusFilter: 'onboarding',
      workReadinessFilter: '',
      showArchived: false,
    };
  }

  if (normalizedPreset === 'READY_TO_WORK') {
    return {
      stagePreset: 'READY_TO_WORK',
      statusFilter: 'onboarding',
      workReadinessFilter: 'READY_TO_WORK',
      showArchived: false,
    };
  }

  if (normalizedPreset === 'CAN_PROMOTE') {
    return {
      stagePreset: 'CAN_PROMOTE',
      statusFilter: 'onboarding',
      workReadinessFilter: 'CAN_PROMOTE',
      showArchived: false,
    };
  }

  if (normalizedPreset === 'ACTIVE') {
    return {
      stagePreset: 'ACTIVE',
      statusFilter: 'active',
      workReadinessFilter: '',
      showArchived: false,
    };
  }

  if (normalizedPreset === 'INACTIVE') {
    return {
      stagePreset: 'INACTIVE',
      statusFilter: 'inactive',
      workReadinessFilter: '',
      showArchived: archived,
    };
  }

  return {
    stagePreset: 'ALL',
    statusFilter: '',
    workReadinessFilter: '',
    showArchived: false,
  };
};

const deriveReadinessFilter = (params) => {
  if (params.get('is_work_ready') === 'true') return 'READY_TO_WORK';
  if (params.get('is_work_ready') === 'false') return 'NOT_READY';
  if (params.get('can_promote') === 'true') return 'CAN_PROMOTE';

  const legacy = params.get('work_readiness');
  if (legacy === 'ready_to_work' || legacy === 'READY_TO_WORK') return 'READY_TO_WORK';
  if (legacy === 'not_ready' || legacy === 'NOT_READY') return 'NOT_READY';
  if (legacy === 'supervised_start' || legacy === 'READY_WITH_CONDITIONS') return 'CAN_PROMOTE';
  return '';
};

export const deriveEmployeePageFilterState = (search = '') => {
  const params = search instanceof URLSearchParams
    ? search
    : new URLSearchParams(normalizeSearch(search));
  const archived = matchesValue(params.get('archived'), 'true');
  const stagePreset = String(params.get('stage_preset') || 'ALL').toUpperCase();
  const presetFilters = deriveStagePresetFilterState(stagePreset, { archived });

  if (stagePreset && stagePreset !== 'ALL') {
    return {
      search: params.get('q') || '',
      viewMode: params.get('view') === 'compliance' ? 'compliance' : 'staff',
      complianceStatusFilter: params.get('compliance_status') || 'all',
      onboardingStatusFilter: params.get('onboarding') || '',
      requirementFilter: params.get('requirement') || '',
      ...presetFilters,
    };
  }

  if (matchesValue(params.get('can_promote'), 'true')) {
    return {
      search: params.get('q') || '',
      viewMode: params.get('view') === 'compliance' ? 'compliance' : 'staff',
      complianceStatusFilter: params.get('compliance_status') || 'all',
      statusFilter: 'onboarding',
      onboardingStatusFilter: params.get('onboarding') || '',
      workReadinessFilter: 'CAN_PROMOTE',
      stagePreset: 'ALL',
      requirementFilter: params.get('requirement') || '',
      showArchived: false,
    };
  }

  return {
    search: params.get('q') || '',
    viewMode: params.get('view') === 'compliance' ? 'compliance' : 'staff',
    complianceStatusFilter: params.get('compliance_status') || 'all',
    statusFilter: params.get('status') || '',
    onboardingStatusFilter: params.get('onboarding') || '',
    workReadinessFilter: deriveReadinessFilter(params),
    stagePreset: 'ALL',
    requirementFilter: params.get('requirement') || '',
    showArchived: archived,
  };
};

export const resolvePeopleWorkspaceView = (pathname = '', search = '') => {
  if (pathname.startsWith(STAFF_INTRODUCTIONS_ROUTE)) {
    return 'staff_profiles';
  }

  if (pathname.startsWith(STAFF_COMMUNICATIONS_ROUTE)) {
    return 'communications';
  }

  if (pathname.startsWith(RECRUITMENT_ROUTE)) {
    return 'applicants';
  }

  if (!pathname.startsWith(EMPLOYEE_ROUTE)) {
    return 'employees';
  }

  const params = new URLSearchParams(normalizeSearch(search));
  const stagePreset = String(params.get('stage_preset') || '').toUpperCase();
  const status = String(params.get('status') || '').toLowerCase();
  const archived = matchesValue(params.get('archived'), 'true');
  const onboarding = params.get('onboarding');
  const canPromote = matchesValue(params.get('can_promote'), 'true');

  if (stagePreset === 'INACTIVE' || archived || status === 'inactive' || status === 'archived') {
    return 'inactive';
  }

  if (
    stagePreset === 'ONBOARDING' ||
    stagePreset === 'READY_TO_WORK' ||
    stagePreset === 'CAN_PROMOTE' ||
    canPromote ||
    status === 'onboarding' ||
    (onboarding && !matchesValue(onboarding, 'all'))
  ) {
    return 'onboarding';
  }

  return 'employees';
};

export const getPeopleWorkspaceView = (viewId) => (
  PEOPLE_WORKSPACE_VIEWS.find((view) => view.id === viewId) || PEOPLE_WORKSPACE_VIEWS[2]
);
